<template>
    <div class="w-full">
        <div class="flex flex-wrap justify-between">
            <div class="w-full px-3 mt-5">
                <div
                    class="flex flex-col sm:flex-row sm:items-center gap-4 mb-4"
                >
                    <div class="flex items-center gap-2">
                        <span>{{ $t("jsonSerializer.jsonFormat") }}</span>
                        <checkbox-component v-model:value="needJson" />
                    </div>
                    <div class="flex items-center gap-2" v-if="!needJson">
                        <span>{{ $t("jsonSerializer.escapeQuotes") }}</span>
                        <checkbox-component v-model:value="needEscapeQuotes" />
                    </div>
                </div>
            </div>

            <div class="md:w-1/2 w-full px-3 mt-2">
                <span class="font-semibold text-lg block mb-2">
                    {{ $t("json.input") }}
                </span>
                <code-editor v-model:value="input" languages="json" />
            </div>

            <div class="w-full md:hidden flex justify-center mt-4 px-3">
                <button
                    :disabled="isProcessing"
                    @click="processFile"
                    class="flex items-center gap-2 border border-primary-primary hover:bg-white bg-primary-primary text-white hover:text-primary-primary font-bold py-2 px-6 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                    <span>
                        {{
                            isProcessing
                                ? $t("common.processing")
                                : $t("common.process")
                        }}
                    </span>
                </button>
            </div>

            <div id="json-serialize-result" class="md:w-1/2 w-full px-3 mt-2">
                <div class="flex flex-col">
                    <span class="font-semibold text-lg mb-2">{{
                        $t("json.output")
                    }}</span>
                    <span
                        v-if="reasonForInvalidity"
                        class="text-yellow-500 text-base mt-2 md:hidden"
                    >
                        {{ $t("jsonSerializer.invalidJson") }}:
                        {{ reasonForInvalidity }}
                    </span>
                </div>
                <code-editor v-model:value="result" read-only />
            </div>
        </div>

        <div class="hidden md:flex flex-col items-center px-3 mt-6 space-y-2">
            <button
                :disabled="isProcessing"
                @click="processFile"
                class="flex items-center gap-2 border border-primary-primary hover:bg-white bg-primary-primary text-white hover:text-primary-primary font-bold py-2 px-6 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
                <span>
                    {{
                        isProcessing
                            ? $t("common.processing")
                            : $t("common.process")
                    }}
                </span>
            </button>

            <span
                v-if="reasonForInvalidity"
                class="text-yellow-500 text-base text-center"
            >
                {{ $t("jsonSerializer.invalidJson") }}:
                {{ reasonForInvalidity }}
            </span>
        </div>
    </div>
</template>

<script lang="ts">
import type { JsonAdapter } from "inversify-utils-kit";
import { defineComponent, inject, reactive, ref, toRefs } from "vue";

import CheckboxComponent from "@/components/utils/CheckboxComponent.vue";
import CodeEditor from "@/components/utils/CodeEditor.vue";

export default defineComponent({
    components: { CodeEditor, CheckboxComponent },
    setup() {
        const jsonAdapter = inject("jsonAdapter") as JsonAdapter;
        const needJson = ref(false);
        const needEscapeQuotes = ref(true);

        const state = reactive({
            input: "{}",
            result: "",
            reasonForInvalidity: "",
            isProcessing: false
        });

        const goToResultContainer = () => {
            const section = document.getElementById("json-serialize-result");
            if (section) {
                section.scrollIntoView({ behavior: "smooth" });
            }
        };

        const processFile = async () => {
            state.isProcessing = true;
            state.reasonForInvalidity = "";
            state.result = "";
            await new Promise((res) => setTimeout(res, 300));
            const jsonFormatter = jsonAdapter.parseStringToJson(state.input);
            if (jsonFormatter.reasonForInvalidity) {
                state.reasonForInvalidity = jsonFormatter.reasonForInvalidity;
                state.isProcessing = false;
                return;
            }
            if (needJson.value) {
                const jsonSerialize = jsonAdapter.serializeJson(
                    jsonFormatter.result,
                    false
                ).result;
                const jsonParsed = jsonAdapter.parseJsonToString(
                    { result: jsonSerialize },
                    false
                ).result;
                state.result = jsonParsed;
            } else {
                const jsonSerialize = jsonAdapter.serializeJson(
                    jsonFormatter.result,
                    needEscapeQuotes.value
                ).result;
                state.result = jsonSerialize;
            }
            if (needEscapeQuotes.value) {
                state.result = `"${state.result}"`;
            }
            goToResultContainer();
            state.isProcessing = false;
        };

        return { processFile, ...toRefs(state), needEscapeQuotes, needJson };
    }
});
</script>
